/**
 * AI Match Modal Component
 * Shows explanation about AI-based pet matching on first interaction
 * Handles survey check and localStorage flag
 */

import React, { useCallback, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { useAuth } from '@/hooks/useAuth';

const AI_MODAL_SHOWN_KEY = 'drug_ai_match_modal_shown';

type ModalState = 'intro' | 'no_survey' | 'closed';

interface AIMatchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when user acknowledges and survey is already completed - should apply AI filters */
  onComplete?: () => void;
}

export const AIMatchModal: React.FC<AIMatchModalProps> = ({ open, onOpenChange, onComplete }) => {
  const navigate = useSafeNavigate();
  const { surveyCompleted } = useAuth();
  const [modalState, setModalState] = useState<ModalState>('intro');

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setModalState('intro');
    }
  }, [open]);

  const handleClose = useCallback((shouldApplyFilters = false) => {
    // Mark modal as shown in localStorage
    localStorage.setItem(AI_MODAL_SHOWN_KEY, 'true');
    setModalState('closed');
    onOpenChange(false);
    
    // If survey completed and user clicked "Понятно!", apply filters
    if (shouldApplyFilters && onComplete) {
      onComplete();
    }
  }, [onOpenChange, onComplete]);

  const handleGoToSurvey = useCallback(() => {
    handleClose(false);
    navigate('/quiz');
  }, [handleClose, navigate]);

  const handleDeclineSurvey = useCallback(() => {
    setModalState('no_survey');
  }, []);

  // Check if modal was already shown
  const wasShown = localStorage.getItem(AI_MODAL_SHOWN_KEY) === 'true';

  // If survey is completed, show intro then close and apply filters
  // If not, show intro then ask about survey
  const handleContinue = useCallback(() => {
    if (surveyCompleted) {
      handleClose(true); // Pass true to trigger onComplete
    } else {
      // Ask about filling survey
      setModalState('no_survey');
    }
  }, [surveyCompleted, handleClose]);
  
  // Close without applying filters (for decline or X button)
  const handleCloseWithoutApply = useCallback(() => {
    handleClose(false);
  }, [handleClose]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-[340px] rounded-2xl"
        style={{ fontFamily: 'CoFo Gothic, sans-serif' }}
      >
        {modalState === 'intro' && (
          <>
            <DialogHeader>
              <DialogTitle 
                className="text-xl font-bold text-center"
                style={{ color: '#3F2516' }}
              >
                ✨ Умный подбор друга
              </DialogTitle>
              <DialogDescription 
                className="text-center pt-3 text-base"
                style={{ color: '#5C4033' }}
              >
                Здесь работает фильтр на основе искусственного интеллекта и дружбы! 
                Мы подобрали самых пушистых друзей, которые должны вам понравиться 
                на основе вашей анкеты.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex flex-col gap-2 mt-4">
              {!surveyCompleted ? (
                <>
                  <p 
                    className="text-center text-sm mb-2"
                    style={{ color: '#8B7355' }}
                  >
                    Анкета ещё не заполнена. Заполнить сейчас?
                  </p>
                  <div className="flex gap-3 w-full">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={handleDeclineSurvey}
                      style={{ 
                        borderColor: '#3F2516',
                        color: '#3F2516',
                      }}
                    >
                      Нет
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={handleGoToSurvey}
                      style={{ 
                        backgroundColor: '#3F2516',
                        color: 'white',
                      }}
                    >
                      Да
                    </Button>
                  </div>
                </>
              ) : (
                <Button
                  className="w-full"
                  onClick={handleContinue}
                  style={{ 
                    backgroundColor: '#3F2516',
                    color: 'white',
                  }}
                >
                  Понятно!
                </Button>
              )}
            </DialogFooter>
          </>
        )}

        {modalState === 'no_survey' && (
          <>
            <DialogHeader>
              <DialogTitle 
                className="text-xl font-bold text-center"
                style={{ color: '#3F2516' }}
              >
                😢 Ой!
              </DialogTitle>
              <DialogDescription 
                className="text-center pt-3 text-base"
                style={{ color: '#5C4033' }}
              >
                Мы пока слишком мало о вас знаем, чтобы подобрать идеального друга.
                Попробуйте после заполнения анкеты!
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex flex-col gap-2 mt-4">
              <Button
                className="w-full"
                onClick={handleCloseWithoutApply}
                style={{ 
                  backgroundColor: '#3F2516',
                  color: 'white',
                }}
              >
                Хорошо
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                onClick={handleGoToSurvey}
                style={{ color: '#3F2516' }}
              >
                Заполнить анкету
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Hook to check if modal should be shown
export function useAIMatchModal() {
  const [showModal, setShowModal] = useState(false);
  const { surveyCompleted } = useAuth();
  
  const wasShown = useCallback(() => {
    return localStorage.getItem(AI_MODAL_SHOWN_KEY) === 'true';
  }, []);

  // Show modal if: never shown OR survey not completed
  const shouldShowModal = useCallback(() => {
    return !wasShown() || !surveyCompleted;
  }, [wasShown, surveyCompleted]);

  const openModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  return {
    showModal,
    setShowModal,
    openModal,
    closeModal,
    wasShown,
    shouldShowModal,
    surveyCompleted,
  };
}

// Tooltip content for hover
export const AI_MATCH_TOOLTIP_TEXT = 'Умный подбор на основе ИИ и вашей анкеты';

export default AIMatchModal;
